import { ChevronDownIcon } from 'lucide-react';
import { Expanded } from '../expanded/expanded';
import { useExpandedContext } from '../expanded/hooks/use-expanded-context';
import styles from './select.module.css';
import { SelectProps } from './select.props';

export const Select = <T extends string | number>({
	label,
	value,
	options,
	onChange,
}: SelectProps<T>) => {
	return (
		<Expanded
			triggerContent={<SelectTrigger label={label} value={value} options={options} />}
			content={<SelectOptions options={options} onChange={onChange} />}
		/>
	);
};

const SelectTrigger = <T extends string | number>({
	label,
	value,
	options,
}: Pick<SelectProps<T>, 'label' | 'value' | 'options'>) => {
	const selectedValue = options.find((option) => option.value === value);

	return (
		<div className={styles.trigger}>
			<div>
				<p className={styles.label}>{label}</p>
				<p className={styles.selectedValue}>{selectedValue?.label}</p>
			</div>
			<ChevronDownIcon size={22} />
		</div>
	);
};

const SelectOptions = <T extends string | number>({
	options,
	onChange,
}: Pick<SelectProps<T>, 'options' | 'onChange'>) => {
	const { setIsOpen } = useExpandedContext();

	const handleChange = (value: T) => {
		onChange(value);
		setIsOpen(false);
	};

	return (
		<div className={styles.options}>
			{options.map((option) => (
				<div
					key={option.value}
					className={styles.option}
					onClick={() => handleChange(option.value as T)}
				>
					<p className={styles.optionLabel}>{option.label}</p>
					{option.image && <img className={styles.image} src={option.image} />}
				</div>
			))}
		</div>
	);
};
